<template>
  <div>
    <section-collapseable
      ref="section"
      title="Selling Info"
      :sub-title="subtitle"
      :collapseable="isSet"
      hide-initial-when-expanded
      :editable="isSet"
      :is-loading="isLoading"
      @edit="editSellingInfo"
    >
      <!-- Default Section -->
      <selling-info-wholesale
        v-if="isWholesale && sellingWholesaleInfo"
        :selling-info="sellingWholesaleInfo"
        is-summary
      ></selling-info-wholesale>

      <selling-info-flip
        v-else-if="isFlip && sellingFlipInfo"
        :selling-info="sellingFlipInfo"
        is-summary
      ></selling-info-flip>

      <div v-else>
        <form-field-select
          label="Would you like to sell via wholesale or fix & flip?"
          :value="analysisType"
          @input="setAnalysisType"
          :loading="typeIsLoading"
          :disabled="typeIsLoading"
        >
          <option :value="null">None</option>
          <option
            v-for="type in ENUMS.SellingInfoTypeEnum"
            :key="type.value"
            :value="type.value"
          >{{ type.label }}</option>
        </form-field-select>
      </div>

      <!-- Expanded Section -->
      <template v-slot:expanded>
        <form-field-select
          label="Selling Type"
          :value="analysisType"
          @input="setAnalysisType"
          :loading="typeIsLoading"
          :disabled="typeIsLoading"
        >
          <option :value="null">None</option>
          <option
            v-for="type in ENUMS.SellingInfoTypeEnum"
            :key="type.value"
            :value="type.value"
          >{{ type.label }}</option>
        </form-field-select>
        <hr>

        <selling-info-wholesale
          v-if="isWholesale && sellingWholesaleInfo"
          :selling-info="sellingWholesaleInfo"
        ></selling-info-wholesale>
        <selling-info-flip
          v-if="isFlip && sellingFlipInfo"
          :selling-info="sellingFlipInfo"
        ></selling-info-flip>
      </template>
    </section-collapseable>

    <!-- Modals -->
    <modal-container>
      <modal-crud-form
        ref="flipSellingModal"
        title="Selling Info"
        form-component="flip-selling-info-form"
        hide-delete-button
      ></modal-crud-form>

      <modal-crud-form
        ref="wholesaleSellingModal"
        title="Selling Info"
        form-component="wholesale-selling-info-form"
        hide-delete-button
      ></modal-crud-form>
    </modal-container>
  </div>
</template>

<script>
import {ENUMS} from "../../../services/enums";
import InfoItem from "../components/InfoItem";
import AnalysisItem from "../components/AnalysisItem";
import SummaryItem from "../components/SummaryItem";
import ProfitItem from "../components/ProfitItem";
import InfoHeading from "../components/InfoHeading";
import SellingInfoWholesale from "../components/SellingInfoWholesale";
import SellingInfoFlip from "../components/SellingInfoFlip";
import api from "../../../api";

export default {
  name: "LeadSellingInfo",
  components: {
    SellingInfoFlip,
    SellingInfoWholesale,
    InfoHeading,
    ProfitItem,
    SummaryItem,
    AnalysisItem,
    InfoItem
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      typeIsLoading: false,
    }
  },
  computed: {
    record() {
      // If activeRecord is null then return necessary empty info
      return this.$store.state.activeRecord ? this.$store.state.activeRecord.record : {
        selling_info_type: null,
      };
    },
    analysisType() {
      return this.record.selling_info_type;
    },
    isNotSet() {
      return this.analysisType === null;
    },
    isSet() {
      return !!this.analysisType;
    },
    isFlip() {
      return this.analysisType === ENUMS.SellingInfoTypeEnum.FixAndFlip.value;
    },
    isWholesale() {
      return this.analysisType === ENUMS.SellingInfoTypeEnum.Wholesale.value;
    },
    subtitle() {
      if(this.isFlip) {
        return ENUMS.SellingInfoTypeEnum.FixAndFlip.label;
      }

      if(this.isWholesale) {
        return ENUMS.SellingInfoTypeEnum.Wholesale.label;
      }

      return '';
    },
    sellingFlipInfo() {
      return this.$store.state.activeRecord ? this.$store.state.activeRecord.fixAndFlipSellingInfo : null;
    },
    sellingWholesaleInfo() {
      return this.$store.state.activeRecord ? this.$store.state.activeRecord.wholesaleSellingInfo : null;
    }
  },
  methods: {
    setAnalysisType(type) {
      this.typeIsLoading = true;

      if (type === null) {
        api.records.setActiveSellingInfo(this.record.id, {})
          .finally(() => {
            this.typeIsLoading = false;
            this.$refs.section.toggleSection();
          });
      }

      if (type === ENUMS.SellingInfoTypeEnum.FixAndFlip.value) {
        if (this.sellingFlipInfo) {
          api.records.setActiveSellingInfo(this.record.id, this.sellingFlipInfo)
            .finally(() => { this.typeIsLoading = false; });
        } else {
          api.fixAndFlipSellingInfos.create({
            record_id: this.record.id,
            accepted_at: null,
            closing_at: null,
            contracted_at: null,
            sale_amount: null,
            status: null,
            total_costs: null,
          }).finally(() => { this.typeIsLoading = false; });
        }
      }

      if (type === ENUMS.SellingInfoTypeEnum.Wholesale.value) {
        if (this.sellingWholesaleInfo) {
          api.records.setActiveSellingInfo(this.record.id, this.sellingWholesaleInfo)
            .finally(() => { this.typeIsLoading = false; });
        } else {
          api.wholesaleSellingInfos.create({
            record_id: this.record.id,
            assigned_at: null,
            closing_at: null,
            earnest_amount: null,
            earnest_money_deposited: false,
            inspect_by_date: null,
            sale_amount: null,
            status: null,
          }).finally(() => { this.typeIsLoading = false; });
        }
      }
    },
    editSellingInfo() {
      if(this.isWholesale) {
        if(this.sellingWholesaleInfo) {
          this.$refs.wholesaleSellingModal.open(this.sellingWholesaleInfo);
        } else {
          this.$refs.wholesaleSellingModal.open({
            record_id: this.record.id
          });
        }
      }

      if(this.isFlip) {
        if(this.sellingFlipInfo) {
          this.$refs.flipSellingModal.open(this.sellingFlipInfo);
        } else {
          this.$refs.flipSellingModal.open({
            record_id: this.record.id
          });
        }
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

hr {
  margin: 0.75rem (-$modalGutter);
}
</style>
